<template>
  <div class="return-policy-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Section -->
    <div class="return-hero">
      <div class="hero-content">
        <div class="policy-icon">
          <span class="icon-emoji">📦</span>
          <div class="icon-badge">
            <i class="fa-solid fa-rotate-left"></i>
          </div>
        </div>
        <h1>IPA Store Return Policy</h1>
        <p>
          Fair, transparent returns for <strong>IPA Store</strong> orders — premium sports gear sold directly by
          <strong>IPromiseAfrica</strong>. This policy does <strong>not</strong> apply to the resale marketplace,
          where buyers and sellers arrange their own terms.
        </p>
        <div class="last-updated">
          <i class="fa-solid fa-calendar"></i>
          Last Updated: December 27, 2025
        </div>
        <div class="applicability-badge">
          <i class="fa-solid fa-store"></i>
          Applies to: IPA Store purchases only
        </div>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Quick Summary -->
    <div class="quick-summary">
      <div class="summary-content">
        <div class="summary-card key-info">
          <div class="summary-icon">
            <i class="fa-solid fa-clock"></i>
          </div>
          <div class="summary-text">
            <h4>14-Day Return Period</h4>
            <p>Returns must be initiated within 14 days of delivery</p>
          </div>
        </div>
        <div class="summary-card eligibility">
          <div class="summary-icon">
            <i class="fa-solid fa-check-circle"></i>
          </div>
          <div class="summary-text">
            <h4>Clear Eligibility Rules</h4>
            <p>Know when you can and cannot return items</p>
          </div>
        </div>
        <div class="summary-card refund">
          <div class="summary-icon">
            <i class="fa-solid fa-money-bill-wave"></i>
          </div>
          <div class="summary-text">
            <h4>7-Day Refund Processing</h4>
            <p>Refunds issued within 7 business days after inspection</p>
          </div>
        </div>
        <div class="summary-card scope-note">
          <div class="summary-icon">
            <i class="fa-solid fa-shop"></i>
          </div>
          <div class="summary-text">
            <h4>IPA Store only</h4>
            <p>Resale marketplace purchases are excluded — parties negotiate on their own; IPA is not liable</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="policy-container">
      <div class="policy-content">
        <!-- IPA Store vs resale -->
        <section class="policy-section scope-section" data-aos="fade-up">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">🏬</span>
            </div>
            <h2>IPA Store and the resale marketplace</h2>
            <div class="section-badge info">
              <i class="fa-solid fa-circle-info"></i>
              Read this first
            </div>
          </div>
          <div class="section-body">
            <p class="section-intro">
              IPromiseAfrica offers two different ways to shop. <strong>Only IPA Store orders are covered by this return policy.</strong>
            </p>
            <div class="disclaimer-content scope-split">
              <div class="disclaimer-point">
                <div class="point-icon">
                  <i class="fa-solid fa-store"></i>
                </div>
                <div class="point-content">
                  <h4>IPA Store</h4>
                  <p>
                    Our official store for premium sports gear. Products are sold by <strong>IPromiseAfrica</strong> (first-party retail).
                    The timelines, eligibility rules, shipping notes, and support options on this page apply to IPA Store purchases.
                  </p>
                </div>
              </div>
              <div class="disclaimer-point">
                <div class="point-icon">
                  <i class="fa-solid fa-handshake-simple"></i>
                </div>
                <div class="point-content">
                  <h4>Resale marketplace</h4>
                  <p>
                    A separate area where independent buyers and sellers connect. Returns, refunds, and any after-sale issues are
                    <strong>negotiated directly between those parties</strong>. This page does not set policy for resale, and
                    <strong>IPromiseAfrica (IPA) is not liable</strong> for resale transactions, including product quality, delivery, or disputes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Return Period -->
        <section class="policy-section" data-aos="fade-up">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">📅</span>
            </div>
            <h2>Return Period</h2>
            <div class="section-badge important">
              <i class="fa-solid fa-clock"></i>
              14 Days
            </div>
          </div>
          <div class="section-body">
            <p>
              For <strong>IPA Store</strong> purchases, all eligible items must be returned within <strong>14 days</strong> from the
              date of delivery or pickup.
            </p>
            <div class="timeline">
              <div class="timeline-item">
                <div class="timeline-marker delivery">
                  <i class="fa-solid fa-truck"></i>
                </div>
                <div class="timeline-content">
                  <h5>Day 1: Delivery/Pickup</h5>
                  <p>Your return window starts immediately</p>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-marker deadline">
                  <i class="fa-solid fa-calendar-xmark"></i>
                </div>
                <div class="timeline-content">
                  <h5>Day 14: Last Day to Return</h5>
                  <p>Return request must be submitted by end of day</p>
                </div>
              </div>
            </div>
            <div class="section-note">
              <i class="fa-solid fa-lightbulb"></i>
              <p><strong>Tip:</strong> Initiate returns as soon as possible to avoid missing the deadline.</p>
            </div>
          </div>
        </section>

        <!-- Eligibility for Returns -->
        <section class="policy-section" data-aos="fade-up" data-aos-delay="100">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">🛍️</span>
            </div>
            <h2>Eligibility for Returns</h2>
            <div class="section-badge success">
              <i class="fa-solid fa-check"></i>
              Allowed
            </div>
          </div>
          <div class="section-body">
            <p class="section-intro">For <strong>IPA Store</strong> orders, a return is accepted only if:</p>
            <div class="eligibility-cards">
              <div class="eligibility-card allowed">
                <div class="card-header">
                  <div class="card-icon">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                  </div>
                  <h4>Mismatched Description</h4>
                </div>
                <p>The product does not match the IPA Store listing or description</p>
                <div class="card-example">
                  <i class="fa-solid fa-info-circle"></i>
                  <span><strong>Example:</strong> Listed as "new" but arrived used</span>
                </div>
              </div>
              <div class="eligibility-card allowed">
                <div class="card-header">
                  <div class="card-icon">
                    <i class="fa-solid fa-screwdriver-wrench"></i>
                  </div>
                  <h4>Defective or Damaged</h4>
                </div>
                <p>The product is defective or damaged upon arrival</p>
                <div class="card-example">
                  <i class="fa-solid fa-info-circle"></i>
                  <span><strong>Example:</strong> Broken equipment or faulty electronics</span>
                </div>
              </div>
              <div class="eligibility-card allowed">
                <div class="card-header">
                  <div class="card-icon">
                    <i class="fa-solid fa-boxes-packing"></i>
                  </div>
                  <h4>Incorrect Item</h4>
                </div>
                <p>The product delivered is different from what was ordered</p>
                <div class="card-example">
                  <i class="fa-solid fa-info-circle"></i>
                  <span><strong>Example:</strong> Ordered football, received basketball</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Non-Returnable Items -->
        <section class="policy-section" data-aos="fade-up" data-aos-delay="200">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">❌</span>
            </div>
            <h2>Non-Returnable Items</h2>
            <div class="section-badge error">
              <i class="fa-solid fa-ban"></i>
              Not Allowed
            </div>
          </div>
          <div class="section-body">
            <p class="section-intro">For <strong>IPA Store</strong> orders, returns will not be accepted if:</p>
            <div class="non-returnable-grid">
              <div class="non-returnable-item">
                <div class="item-icon">
                  <i class="fa-solid fa-heart-circle-xmark"></i>
                </div>
                <div class="item-content">
                  <h4>Changed Mind</h4>
                  <p>You simply changed your mind about the purchase</p>
                </div>
              </div>
              <div class="non-returnable-item">
                <div class="item-icon">
                  <i class="fa-solid fa-person-circle-exclamation"></i>
                </div>
                <div class="item-content">
                  <h4>Misuse or Wear</h4>
                  <p>Item has clear signs of misuse, wear, or damage after delivery</p>
                </div>
              </div>
              <div class="non-returnable-item">
                <div class="item-icon">
                  <i class="fa-solid fa-box-open"></i>
                </div>
                <div class="item-content">
                  <h4>Missing Packaging</h4>
                  <p>Tags, original packaging, or accessories are missing</p>
                </div>
              </div>
            </div>
            <div class="section-warning">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <div>
                <p><strong>Important:</strong> Items must be returned in the same condition as received.</p>
                <p class="warning-subtext">If original packaging is damaged or missing, returns may not be accepted.</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Return Shipping Costs -->
        <section class="policy-section" data-aos="fade-up" data-aos-delay="500">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">🚚</span>
            </div>
            <h2>Return Shipping Costs</h2>
            <div class="section-badge important">
              <i class="fa-solid fa-ship"></i>
              Who Pays?
            </div>
          </div>
          <div class="section-body">
            <p class="section-intro">For <strong>IPA Store</strong> returns, who pays return shipping depends on the reason:</p>
            <div class="shipping-responsibility">
              <div class="responsibility-card seller-fault">
                <div class="responsibility-header">
                  <i class="fa-solid fa-store"></i>
                  <h4>IPA Store / product issue</h4>
                  <span class="cost-badge free">Free Return</span>
                </div>
                <ul class="fault-reasons">
                  <li>Wrong item delivered</li>
                  <li>Damaged item received</li>
                  <li>Item not as described on IPA Store</li>
                  <li>Fulfilment error by IPA Store</li>
                </ul>
                <div class="responsibility-note">
                  <i class="fa-solid fa-truck-fast"></i>
                  <span>Return shipping is covered by IPA Store</span>
                </div>
              </div>
              <div class="responsibility-card buyer-fault">
                <div class="responsibility-header">
                  <i class="fa-solid fa-user"></i>
                  <h4>Buyer's Choice</h4>
                  <span class="cost-badge paid">Buyer Pays</span>
                </div>
                <ul class="fault-reasons">
                  <li>Wrong size chosen</li>
                  <li>Changed mind</li>
                  <li>Ordered by mistake</li>
                  <li>No longer needed</li>
                </ul>
                <div class="responsibility-note">
                  <i class="fa-solid fa-money-bill-wave"></i>
                  <span>Return shipping costs are paid by the buyer</span>
                </div>
              </div>
            </div>
            <div class="shipping-estimate">
              <h5><i class="fa-solid fa-calculator"></i> Shipping Cost Estimate</h5>
              <p>Return shipping typically costs between KES 150 - KES 1000 depending on size and weight.</p>
            </div>
          </div>
        </section>

        <!-- Resale marketplace disclaimer -->
        <section class="policy-section disclaimer-section" data-aos="fade-up" data-aos-delay="600">
          <div class="section-header">
            <div class="section-icon">
              <span class="emoji">🛡️</span>
            </div>
            <h2>Resale marketplace — not covered here</h2>
            <div class="section-badge warning">
              <i class="fa-solid fa-scale-balanced"></i>
              Important Notice
            </div>
          </div>
          <div class="section-body">
            <div class="disclaimer-content">
              <div class="disclaimer-point">
                <div class="point-icon">
                  <i class="fa-solid fa-people-arrows"></i>
                </div>
                <div class="point-content">
                  <h4>Direct negotiation</h4>
                  <p>
                    On the resale marketplace, buyers and sellers deal with each other. Returns, refunds, warranties, and dispute
                    outcomes are for those parties to agree — not governed by this IPA Store return policy.
                  </p>
                </div>
              </div>
              <div class="disclaimer-point">
                <div class="point-icon">
                  <i class="fa-solid fa-scale-balanced"></i>
                </div>
                <div class="point-content">
                  <h4>No IPA liability for resale</h4>
                  <p>
                    <strong>IPromiseAfrica (IPA) is not liable</strong> for resale marketplace transactions, including product
                    accuracy, condition, delivery, payment disputes, or how buyers and sellers resolve issues between themselves.
                  </p>
                </div>
              </div>
              <div class="disclaimer-point">
                <div class="point-icon">
                  <i class="fa-solid fa-file-circle-xmark"></i>
                </div>
                <div class="point-content">
                  <h4>This page is IPA Store only</h4>
                  <p>
                    The rules above (return period, eligibility, shipping responsibility, and processing times) apply only to
                    purchases from the IPA Store. They do not create obligations for resale marketplace deals.
                  </p>
                </div>
              </div>
            </div>
            <div class="disclaimer-warning">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <div>
                <p>
                  <strong>Important:</strong> For transactions conducted outside IPA's platform or outside the IPA Store checkout,
                  we cannot guarantee the same protections or dispute handling described on this page.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Need Help Section -->
        <section class="help-section" data-aos="fade-up" data-aos-delay="800">
          <div class="help-content">
            <div class="help-header">
              <i class="fa-solid fa-headset"></i>
              <h3>Need Help with a Return?</h3>
            </div>
            <p>Our support team can help with <strong>IPA Store</strong> returns and refunds. For resale marketplace issues, contact the seller you bought from.</p>
            <div class="help-actions">
              <a href="tel:+254791869346" class="help-btn outline">
                <i class="fa-solid fa-phone"></i>
                Call Support: +254 791 869 346
              </a>
            </div>
            <div class="response-time">
              <i class="fa-solid fa-clock"></i>
              <span>Average response time: 2-4 hours during business hours</span>
            </div>
          </div>
        </section>

        <!-- Policy Updates -->
        <div class="policy-updates">
          <div class="updates-content">
            <i class="fa-solid fa-bullhorn"></i>
            <div>
              <h5>Policy Updates</h5>
              <p>We may update this policy periodically. Check the "Last Updated" date at the top for the latest version.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";
import AOS from 'aos';
import 'aos/dist/aos.css';

export default {
  name: "ReturnPolicy",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  mounted() {
    // Initialize animations
    AOS.init({
      duration: 600,
      once: true,
      offset: 100
    });
    
    // Scroll to top
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
/* ================= HERO SECTION ================= */
.return-hero {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  color: white;
  padding: 100px 20px 60px;
  text-align: center;
  margin-top: 70px;
  position: relative;
  overflow: hidden;
}

.return-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
}

.return-hero .hero-content {
  position: relative;
  z-index: 2;
}

.policy-icon {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
}

.icon-emoji {
  font-size: 80px;
  display: block;
  line-height: 1;
}

.icon-badge {
  position: absolute;
  bottom: -5px;
  right: -5px;
  background: #ff9800;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #1a237e;
}

.icon-badge i {
  color: white;
  font-size: 1.2rem;
}

.hero-content h1 {
  font-size: 2.8rem;
  font-weight: 800;
  margin-bottom: 15px;
  background: linear-gradient(90deg, #bbdefb, #90caf9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-content > p {
  font-size: 1.1rem;
  color: #bbdefb;
  max-width: 720px;
  margin: 0 auto 20px;
}

.last-updated {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #90caf9;
  margin: 10px;
}

.applicability-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #90caf9;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin: 10px;
}

/* ================= QUICK SUMMARY ================= */
.quick-summary {
  background: white;
  padding: 40px 20px;
  margin: -30px auto 0;
  max-width: 1200px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.summary-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px;
  border-radius: 15px;
  border: 2px solid;
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.summary-card.key-info {
  border-color: #2196f3;
  background: #e3f2fd;
}

.summary-card.eligibility {
  border-color: #4caf50;
  background: #e8f5e9;
}

.summary-card.refund {
  border-color: #ff9800;
  background: #fff3e0;
}

.summary-card.scope-note {
  border-color: #7e57c2;
  background: #ede7f6;
}

.scope-note .summary-icon {
  background: #7e57c2;
  color: white;
}

.summary-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
}

.key-info .summary-icon {
  background: #2196f3;
  color: white;
}

.eligibility .summary-icon {
  background: #4caf50;
  color: white;
}

.refund .summary-icon {
  background: #ff9800;
  color: white;
}

.summary-text h4 {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  color: #1a237e;
}

.summary-text p {
  margin: 0;
  color: #37474f;
  font-size: 0.95rem;
}

/* ================= POLICY SECTIONS ================= */
.policy-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.policy-section {
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.disclaimer-section {
  border: 2px solid #ff9800;
  background: #fff8e1;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e8eaf6;
  flex-wrap: wrap;
}

.section-icon {
  font-size: 2.5rem;
}

.section-header h2 {
  flex: 1;
  margin: 0;
  color: #1a237e;
  font-size: 1.8rem;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.section-badge.important {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #2196f3;
}

.section-badge.success {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #4caf50;
}

.section-badge.error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #f44336;
}

.section-badge.info {
  background: #e0f7fa;
  color: #006064;
  border: 1px solid #00bcd4;
}

.section-badge.warning {
  background: #fff3e0;
  color: #ef6c00;
  border: 1px solid #ff9800;
}

/* ================= SECTION BODY ================= */
.section-body {
  color: #37474f;
}

.section-intro {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 25px;
  color: #1a237e;
}

/* ================= TIMELINE ================= */
.timeline {
  display: flex;
  gap: 40px;
  margin: 30px 0;
  flex-wrap: wrap;
}

.timeline-item {
  flex: 1;
  min-width: 250px;
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.timeline-marker {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.timeline-marker.delivery {
  background: #4caf50;
}

.timeline-marker.deadline {
  background: #f44336;
}

.timeline-content h5 {
  margin: 0 0 8px 0;
  color: #1a237e;
  font-size: 1.1rem;
}

.timeline-content p {
  margin: 0;
  color: #666;
  font-size: 0.95rem;
}

.section-note {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px;
  background: #e3f2fd;
  border-radius: 10px;
  margin-top: 20px;
  border: 1px solid #bbdefb;
}

.section-note i {
  color: #2196f3;
  margin-top: 2px;
}

.section-note p {
  margin: 0;
  color: #1565c0;
}

/* ================= ELIGIBILITY CARDS ================= */
.eligibility-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin: 30px 0;
}

.eligibility-card {
  background: white;
  border: 2px solid #c8e6c9;
  border-radius: 12px;
  padding: 25px;
  transition: all 0.3s ease;
}

.eligibility-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #4caf50;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.card-header h4 {
  margin: 0;
  color: #1a237e;
  font-size: 1.2rem;
}

.eligibility-card p {
  color: #37474f;
  margin: 0 0 15px 0;
  line-height: 1.5;
}

.card-example {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: #f1f8e9;
  border-radius: 8px;
  border: 1px solid #c5e1a5;
}

.card-example i {
  color: #689f38;
  margin-top: 2px;
}

.card-example span {
  color: #558b2f;
  font-size: 0.9rem;
}

/* ================= NON-RETURNABLE GRID ================= */
.non-returnable-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin: 30px 0;
}

.non-returnable-item {
  display: flex;
  gap: 20px;
  padding: 25px;
  background: #ffebee;
  border-radius: 12px;
  border: 1px solid #ffcdd2;
  align-items: flex-start;
}

.item-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f44336;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.item-content h4 {
  margin: 0 0 10px 0;
  color: #c62828;
  font-size: 1.2rem;
}

.item-content p {
  margin: 0;
  color: #d32f2f;
  font-size: 0.95rem;
}

.section-warning {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: #fff3e0;
  border-radius: 10px;
  border: 2px solid #ffcc80;
  margin-top: 25px;
}

.section-warning i {
  color: #ff9800;
  font-size: 1.5rem;
  margin-top: 2px;
}

.section-warning p {
  margin: 0 0 8px 0;
  color: #ef6c00;
  font-size: 1.05rem;
}

.warning-subtext {
  font-size: 0.95rem !important;
  color: #ff9800 !important;
  margin-bottom: 0 !important;
}

/* ================= SHIPPING RESPONSIBILITY ================= */
.shipping-responsibility {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 30px 0;
}

.responsibility-card {
  padding: 30px;
  border-radius: 15px;
  border: 2px solid;
}

.responsibility-card.seller-fault {
  border-color: #4caf50;
  background: #e8f5e9;
}

.responsibility-card.buyer-fault {
  border-color: #ff9800;
  background: #fff3e0;
}

.responsibility-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.responsibility-header i {
  font-size: 2rem;
  color: #1a237e;
}

.responsibility-header h4 {
  flex: 1;
  margin: 0;
  color: #1a237e;
  font-size: 1.3rem;
}

.cost-badge {
  padding: 6px 15px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.cost-badge.free {
  background: #4caf50;
  color: white;
}

.cost-badge.paid {
  background: #ff9800;
  color: white;
}

.fault-reasons {
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
}

.fault-reasons li {
  padding: 8px 0 8px 25px;
  position: relative;
  color: #37474f;
}

.fault-reasons li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #4caf50;
  font-weight: bold;
}

.buyer-fault .fault-reasons li::before {
  content: '✗';
  color: #ff9800;
}

.responsibility-note {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: white;
  border-radius: 8px;
  border: 1px solid;
}

.seller-fault .responsibility-note {
  border-color: #c8e6c9;
  color: #2e7d32;
}

.buyer-fault .responsibility-note {
  border-color: #ffe082;
  color: #ff8f00;
}

.shipping-estimate {
  padding: 20px;
  background: #f5f5f5;
  border-radius: 10px;
  margin-top: 25px;
}

.shipping-estimate h5 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px 0;
  color: #1a237e;
  font-size: 1.1rem;
}

.shipping-estimate p {
  margin: 0;
  color: #666;
}

/* ================= DISCLAIMER SECTION ================= */
.disclaimer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin: 30px 0;
}

.disclaimer-point {
  display: flex;
  gap: 20px;
  padding: 25px;
  background: white;
  border-radius: 10px;
  border: 1px solid #ffe082;
  align-items: flex-start;
}

.point-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #ff9800;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.point-content h4 {
  margin: 0 0 10px 0;
  color: #e65100;
  font-size: 1.1rem;
}

.point-content p {
  margin: 0;
  color: #ef6c00;
  font-size: 0.95rem;
}

.disclaimer-warning {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: #fff3e0;
  border-radius: 10px;
  border: 2px solid #ffcc80;
  margin-top: 25px;
}

.disclaimer-warning i {
  color: #ff9800;
  font-size: 1.8rem;
  margin-top: 2px;
}

.disclaimer-warning p {
  margin: 0 0 10px 0;
  color: #ef6c00;
  font-size: 1rem;
}

.disclaimer-warning p:last-child {
  margin-bottom: 0;
  font-size: 0.95rem;
}

/* ================= HELP SECTION ================= */
.help-section {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  border-radius: 20px;
  padding: 50px;
  margin: 50px 0;
  text-align: center;
  color: white;
}

.help-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
}

.help-header i {
  font-size: 2.5rem;
  color: #bbdefb;
}

.help-header h3 {
  margin: 0;
  font-size: 1.8rem;
  color: white;
}

.help-section p {
  max-width: 600px;
  margin: 0 auto 30px;
  color: #bbdefb;
  font-size: 1.1rem;
  line-height: 1.6;
}

.help-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.help-btn {
  padding: 15px 30px;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.help-btn.primary {
  background: #4caf50;
  color: white;
}

.help-btn.primary:hover {
  background: #388e3c;
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3);
}

.help-btn.secondary {
  background: white;
  color: #3949ab;
}

.help-btn.secondary:hover {
  background: #f5f7ff;
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(255, 255, 255, 0.2);
}

.help-btn.outline {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.help-btn.outline:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.response-time {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 25px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  color: #bbdefb;
  font-size: 0.9rem;
}

/* ================= POLICY UPDATES ================= */
.policy-updates {
  padding: 20px;
  background: #e3f2fd;
  border-radius: 10px;
  border: 1px solid #bbdefb;
  margin-top: 40px;
}

.updates-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.updates-content i {
  color: #2196f3;
  font-size: 1.8rem;
}

.updates-content h5 {
  margin: 0 0 5px 0;
  color: #0d47a1;
  font-size: 1.1rem;
}

.updates-content p {
  margin: 0;
  color: #1565c0;
  font-size: 0.95rem;
}

/* ================= RESPONSIVE ADJUSTMENTS ================= */
@media (max-width: 768px) {
  .return-hero {
    padding: 80px 15px 40px;
  }
  
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .summary-content {
    grid-template-columns: 1fr;
  }
  
  .policy-section {
    padding: 20px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .section-header h2 {
    font-size: 1.5rem;
  }
  
  .eligibility-cards,
  .non-returnable-grid,
  .shipping-responsibility,
  .disclaimer-content {
    grid-template-columns: 1fr;
  }
  

  .help-section {
    padding: 30px 20px;
  }
  
  .help-actions {
    flex-direction: column;
  }
  
  .help-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .return-hero {
    padding: 60px 10px 30px;
  }
  
  .hero-content h1 {
    font-size: 1.8rem;
  }
  
  .policy-section {
    padding: 15px;
  }
  
  .section-header h2 {
    font-size: 1.3rem;
  }
  
  .timeline {
    flex-direction: column;
  }
}
</style>